// HLS playback for review <video> elements. Safari/iOS take the playlist as
// `src`; everything else goes through hls.js, loaded lazily so non-HLS pages
// don't pay for it. Chromium (141+) has native HLS too, but it has no segment
// retry and reports failures as bare stalls / DEMUXER errors — hls.js retries
// fragments with backoff, so Chromium uses it whenever MSE is available.

const isHlsUrl = (url) => /\.m3u8(\?|$)/i.test(url || '')

let nativeHls = null
const hasNativeHls = () => {
  if (nativeHls === null) {
    const canNative = !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl')
    // ponytail: userAgentData exists only on Chromium — a cheap "not Safari" check.
    const isChromiumWithMse = !!navigator.userAgentData && typeof window.MediaSource !== 'undefined'
    nativeHls = canNative && !isChromiumWithMse
  }
  return nativeHls
}

// True when `url` must be attached with attachHlsJs instead of a `src` attribute.
export const needsHlsJs = (url) => isHlsUrl(url) && !hasNativeHls()

// Attach `url` to `video` via hls.js. `onFatal(data)` fires on unrecoverable
// errors (hls.js reports those itself, not through the element's `error`
// event). Returns a detach function.
export async function attachHlsJs(video, url, onFatal) {
  const { default: Hls } = await import('hls.js')
  if (!Hls.isSupported()) {
    onFatal?.({ type: 'unsupported', details: 'hls.js not supported' })
    return () => {}
  }
  const hls = new Hls()
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (data.fatal) onFatal?.(data)
  })
  hls.loadSource(url)
  hls.attachMedia(video)
  return () => hls.destroy()
}
